import React, { useState } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from "@amzn/awsui-components-react";
import styles from "./style.module.scss";

type FeedbackValue = "THUMBS_UP" | "THUMBS_DOWN";

interface SessionFeedbackProps {
  selected: FeedbackValue | null;
  onSubmit: (feedback: FeedbackValue) => void;
  sessionId: string | null;
  teamName: string;
}

const isProd = (): boolean => window.location.hostname.includes("prod");

const buildBugReportUrl = (
  title: string,
  description: string,
  sessionId: string,
  teamName: string,
): string => {
  const url = new URL("https://t.corp.amazon.com/create/options");
  url.searchParams.set("title", `[Gaia Assistant - UI Bug Report] ${title}`);
  url.searchParams.set("severity", "FIVE");
  url.searchParams.set("category", "Finance Tech");
  url.searchParams.set("type", "Tax-Technology");
  url.searchParams.set("item", "Gaia");
  if (!isProd()) {
    url.searchParams.set("group", "Finance-Tech-Gaia-Test");
  }
  url.searchParams.set("descriptionContentType", "text/amz-markdown-sim");
  url.searchParams.set(
    "description",
    `**Session ID:** ${sessionId}\n` +
      `**Team:** ${teamName}\n\n` +
      `---\n\n` +
      `${description}`,
  );
  return url.toString();
};

const SessionFeedback: React.FC<SessionFeedbackProps> = ({
  selected,
  onSubmit,
  sessionId,
  teamName,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [bugTitle, setBugTitle] = useState("");
  const [bugDescription, setBugDescription] = useState("");
  const [titleError, setTitleError] = useState("");

  const handleOpenModal = () => {
    setBugTitle("");
    setBugDescription("");
    setTitleError("");
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = () => {
    if (!bugTitle.trim()) {
      setTitleError("Title is required.");
      return;
    }
    const url = buildBugReportUrl(
      bugTitle.trim(),
      bugDescription.trim(),
      sessionId ?? "not available",
      teamName,
    );
    window.open(url, "_blank", "noopener,noreferrer");
    setIsModalVisible(false);
  };

  return (
    <>
      <Modal
        visible={isModalVisible}
        onDismiss={handleCancel}
        header="Report a bug"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Title" errorText={titleError}>
            <Input
              value={bugTitle}
              onChange={({ detail }) => {
                setBugTitle(detail.value);
                if (detail.value.trim()) setTitleError("");
              }}
              placeholder="Brief description of the issue"
              ariaLabel="Bug report title"
            />
          </FormField>
          <FormField label="Description - optional">
            <Textarea
              value={bugDescription}
              onChange={({ detail }) => setBugDescription(detail.value)}
              placeholder="Provide additional context about the issue"
              ariaLabel="Bug report description"
              rows={4}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <div className={styles.sessionFeedback}>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant={selected === "THUMBS_UP" ? "primary" : "normal"}
            iconName="thumbs-up"
            onClick={() => onSubmit("THUMBS_UP")}
            ariaLabel="Thumbs up"
          />
          <Button
            variant={selected === "THUMBS_DOWN" ? "primary" : "normal"}
            iconName="thumbs-down"
            onClick={() => onSubmit("THUMBS_DOWN")}
            ariaLabel="Thumbs down"
          />
          {selected === "THUMBS_DOWN" && (
            <Button variant="normal" onClick={handleOpenModal}>
              Report Bug
            </Button>
          )}
        </SpaceBetween>
      </div>
    </>
  );
};

export default SessionFeedback;
